import type { TFunction } from 'i18next';
import { AlertTriangle, Clock, FileText, Lock, Star, Users, X } from '@tonnie/assets';
import { teleportalDurationKey } from '@tonnie/types';
import { Badge, cn } from '@tonnie/ui';
import { motion } from 'framer-motion';
import { getCertificationRequirementLabel } from '@/shared/utils/document-helpers';
import { trackVisual, PRIORITY_DOT, type KitTask } from './types';

export function TaskCard({
  task,
  onRemove,
  canRemove,
  dependencyWarning,
  t,
}: {
  task: KitTask;
  onRemove: () => void;
  canRemove: boolean;
  dependencyWarning: string | null;
  t: TFunction;
}) {
  const visual = trackVisual(task.track);

  return (
    <motion.div
      layout
      data-testid={`widget-relocation_kit-task-${task.task_code}`}
      className="flex items-center gap-2 p-2 rounded-md border border-border/50 hover:bg-muted/30 transition-colors group"
    >
      <div className={cn('w-1 h-8 rounded-full shrink-0', visual?.accentTw ?? 'bg-muted')} />

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span
            className={cn(
              'w-1.5 h-1.5 rounded-full shrink-0',
              PRIORITY_DOT[task.priority] ?? PRIORITY_DOT.medium
            )}
          />
          <span className="text-sm font-medium truncate">{t(task.task_name_key)}</span>
          {task.is_required && task.is_blocking && (
            <Badge
              variant="outline"
              className="text-2xs py-0 px-1 border-tone-red-line text-tone-red"
            >
              {t('teleportal.kit.required')}
            </Badge>
          )}
          {task.vault_linked && (
            <Badge
              variant="outline"
              className="text-2xs py-0 px-1 border-tone-green-line text-tone-green"
            >
              {t('teleportal.kit.vaultLinked')}
            </Badge>
          )}
          {task.location && task.location !== 'either' && (
            <Badge
              variant="outline"
              className={cn(
                'text-2xs py-0 px-1',
                task.location === 'home_country' && 'border-tone-blue-line text-tone-blue',
                task.location === 'target_country' && 'border-tone-green-line text-tone-green',
                task.location === 'online' && 'border-tone-violet-line text-tone-violet'
              )}
            >
              {task.location === 'home_country'
                ? t('teleportal.location.homeCountry')
                : task.location === 'target_country'
                  ? t('teleportal.location.targetCountry')
                  : t('teleportal.location.online')}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-2 text-2xs text-muted-foreground mt-0.5">
          {task.requires_document && (
            <span className="flex items-center gap-0.5">
              <FileText className="w-2.5 h-2.5" />
              {task.required_document_types.length} {t('teleportal.kit.docs')}
            </span>
          )}
          <span className="flex items-center gap-0.5">
            <Clock className="w-2.5 h-2.5" />
            {t(teleportalDurationKey(task.estimated_duration))}
          </span>
          {task.miles_reward > 0 && (
            <span className="flex items-center gap-0.5">
              <Star className="w-2.5 h-2.5" />
              {task.miles_reward} {t('teleportal.kit.miles')}
            </span>
          )}
          {task.document_metadata?.some((d) => d.requires_certification) && (
            <span className="flex items-center gap-0.5 text-tone-amber">
              <AlertTriangle className="w-2.5 h-2.5" />
              {getCertificationRequirementLabel(
                task.document_metadata.find((d) => d.requires_certification)?.certification_type,
                t
              )}
            </span>
          )}
          {task.document_metadata?.some((d) => d.validity_days && d.validity_days > 0) && (
            <span className="flex items-center gap-0.5 text-tone-amber">
              <Clock className="w-2.5 h-2.5" />
              {t('teleportal.docDetail.validFor', {
                count: task.document_metadata.find((d) => d.validity_days && d.validity_days > 0)
                  ?.validity_days,
              })}
            </span>
          )}
          {task.household_member_name && (
            <span className="flex items-center gap-0.5">
              <Users className="w-2.5 h-2.5" />
              {task.household_member_name}
            </span>
          )}
        </div>
        {dependencyWarning && (
          <div className="flex items-center gap-1 text-2xs text-tone-amber mt-0.5">
            <AlertTriangle className="w-3 h-3" />
            <span>{dependencyWarning}</span>
          </div>
        )}
      </div>

      {/* Remove / Lock button */}
      {canRemove ? (
        <button
          type="button"
          onClick={onRemove}
          title={t('teleportal.kit.removeTask')}
          className={cn(
            'shrink-0 w-6 h-6 rounded-md flex items-center justify-center transition-colors',
            'text-muted-foreground hover:text-destructive-accessible hover:bg-destructive/10',
            'opacity-0 group-hover:opacity-100 focus-visible:opacity-100'
          )}
          aria-label={`${t('teleportal.kit.removeTask')} ${t(task.task_name_key)}`}
        >
          <X className="w-3.5 h-3.5" />
        </button>
      ) : (
        <div
          className="shrink-0 w-6 h-6 flex items-center justify-center text-muted-foreground/30"
          title={t('teleportal.kit.requiredTask')}
        >
          <Lock className="w-3 h-3" />
        </div>
      )}
    </motion.div>
  );
}
